/**
 * Wire Entry Form Component
 *
 * Form for recording incoming and outgoing wire transfers with
 * optional allocation to shipments.
 */

import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Modal,
  Space,
  Typography,
  Divider,
  message,
  AutoComplete,
  Spin,
  Card,
  Radio,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  DollarOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bankTransactionsApi } from '../../services/api';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

interface ShipmentSearchResult {
  id: string;
  reference: string;
  contract?: {
    reference: string;
    buyer?: { id: string; name: string; shortName: string };
    supplier?: { id: string; name: string; shortName: string };
  };
}

interface WireEntryFormProps {
  /** Open modal with pre-selected shipment */
  shipmentId?: string;
  /** Called when wire is successfully created */
  onSuccess?: (wire: any) => void;
  /** Render as a button that opens modal, or inline form */
  mode?: 'button' | 'inline';
  /** Default direction */
  defaultDirection?: 'IN' | 'OUT';
}

const WireEntryForm: React.FC<WireEntryFormProps> = ({
  shipmentId: initialShipmentId,
  onSuccess,
  mode = 'button',
  defaultDirection = 'IN',
}) => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<ShipmentSearchResult | null>(null);
  const [direction, setDirection] = useState<'IN' | 'OUT'>(defaultDirection);
  const queryClient = useQueryClient();

  // Search shipments for allocation
  const { data: shipmentResults, isLoading: searchingShipments } = useQuery({
    queryKey: ['shipment-search', shipmentSearch],
    queryFn: async () => {
      if (!shipmentSearch || shipmentSearch.length < 2) return [];
      const searchTerm = shipmentSearch.replace(/(\.\d{2})$/, '');
      const response = await api.get('/contracts', {
        params: { search: searchTerm, limit: 10, include: 'shipments' },
      });
      const shipments: ShipmentSearchResult[] = [];
      for (const contract of (response as any).contracts || []) {
        for (const shipment of contract.shipments || []) {
          shipments.push({
            id: shipment.id,
            reference: shipment.reference || `${contract.reference}-${shipment.id.slice(-4)}`,
            contract: {
              reference: contract.reference,
              buyer: contract.buyer,
              supplier: contract.supplier,
            },
          });
        }
      }
      return shipments;
    },
    enabled: shipmentSearch.length >= 2,
  });

  // Create wire mutation
  const createWireMutation = useMutation({
    mutationFn: (data: Parameters<typeof bankTransactionsApi.createWire>[0]) =>
      bankTransactionsApi.createWire(data),
    onSuccess: (response: any) => {
      message.success(`Wire ${direction === 'IN' ? 'received' : 'sent'} recorded successfully`);
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['shipment-transactions'] });
      form.resetFields();
      setSelectedShipment(null);
      setShipmentSearch('');
      setModalVisible(false);
      onSuccess?.(response);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to record wire transfer');
    },
  });

  // Handle shipment selection
  const handleShipmentSelect = (shipment: ShipmentSearchResult) => {
    setSelectedShipment(shipment);
    setShipmentSearch(shipment.reference);

    // Auto-fill counterparty based on direction
    if (shipment.contract) {
      const party = direction === 'IN' ? shipment.contract.buyer : shipment.contract.supplier;
      if (party) {
        form.setFieldsValue({
          counterparty: party.shortName || party.name,
        });
      }
    }
  };

  // Handle direction change
  const handleDirectionChange = (newDirection: 'IN' | 'OUT') => {
    setDirection(newDirection);
    // Update counterparty based on new direction
    if (selectedShipment?.contract) {
      const party = newDirection === 'IN'
        ? selectedShipment.contract.buyer
        : selectedShipment.contract.supplier;
      if (party) {
        form.setFieldsValue({
          counterparty: party.shortName || party.name,
        });
      }
    }
  };

  // Clear shipment selection
  const clearShipment = () => {
    setSelectedShipment(null);
    setShipmentSearch('');
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    const wireData = {
      direction,
      amount: values.amount,
      date: values.date ? values.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      reference: values.reference,
      counterparty: values.counterparty,
      shipmentId: selectedShipment?.id,
      description: values.description,
    };

    createWireMutation.mutate(wireData);
  };

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        date: dayjs(),
      }}
    >
      {/* Direction Toggle */}
      <Form.Item label="Direction" required>
        <Radio.Group
          value={direction}
          onChange={(e) => handleDirectionChange(e.target.value)}
          buttonStyle="solid"
          size="large"
        >
          <Radio.Button value="IN">
            <ArrowDownOutlined style={{ color: '#52c41a' }} /> Incoming (Received)
          </Radio.Button>
          <Radio.Button value="OUT">
            <ArrowUpOutlined style={{ color: '#f5222d' }} /> Outgoing (Sent)
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Divider />

      {/* Wire Details */}
      <Form.Item
        label="Wire Reference"
        name="reference"
        rules={[{ required: true, message: 'Please enter the wire reference' }]}
        help="Bank reference number or transaction ID"
      >
        <Input placeholder="e.g., WT123456789" />
      </Form.Item>

      <Space size="middle" style={{ display: 'flex', marginBottom: 0 }}>
        <Form.Item
          label="Amount (USD)"
          name="amount"
          rules={[
            { required: true, message: 'Please enter amount' },
            { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
          ]}
          style={{ flex: 1 }}
        >
          <InputNumber
            prefix={<DollarOutlined />}
            style={{ width: '100%' }}
            precision={2}
            min={0.01}
            placeholder="0.00"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value!.replace(/,/g, '') as any}
          />
        </Form.Item>

        <Form.Item
          label="Date"
          name="date"
          rules={[{ required: true, message: 'Please select date' }]}
          style={{ flex: 1 }}
        >
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
      </Space>

      <Form.Item
        label={direction === 'IN' ? 'Received From' : 'Sent To'}
        name="counterparty"
        rules={[{ required: true, message: 'Please enter the counterparty' }]}
        help={direction === 'IN' ? 'Buyer or other party sending funds' : 'Supplier or other party receiving funds'}
      >
        <Input placeholder={direction === 'IN' ? 'Buyer name' : 'Supplier name'} />
      </Form.Item>

      <Divider />

      {/* Shipment Allocation (Optional) */}
      <Form.Item
        label="Allocate to Shipment (Optional)"
        help="Search by contract or shipment reference to allocate this wire"
      >
        <AutoComplete
          value={shipmentSearch}
          onChange={setShipmentSearch}
          options={(shipmentResults || []).map((s) => ({
            value: s.reference,
            label: (
              <div>
                <Text strong>{s.reference}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {s.contract?.reference} | {s.contract?.buyer?.shortName || s.contract?.buyer?.name} / {s.contract?.supplier?.shortName || s.contract?.supplier?.name}
                </Text>
              </div>
            ),
            shipment: s,
          }))}
          onSelect={(_, option: any) => handleShipmentSelect(option.shipment)}
          placeholder="Search shipment..."
          style={{ width: '100%' }}
          notFoundContent={
            searchingShipments ? <Spin size="small" /> :
            shipmentSearch.length < 2 ? 'Type at least 2 characters' : 'No shipments found'
          }
        >
          <Input prefix={<SearchOutlined />} />
        </AutoComplete>
      </Form.Item>

      {selectedShipment && (
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space direction="vertical" size="small">
              <div>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <Text strong>Allocated to: {selectedShipment.reference}</Text>
              </div>
              <Text type="secondary">
                Contract: {selectedShipment.contract?.reference} |
                Buyer: {selectedShipment.contract?.buyer?.shortName || selectedShipment.contract?.buyer?.name || 'N/A'} |
                Supplier: {selectedShipment.contract?.supplier?.shortName || selectedShipment.contract?.supplier?.name || 'N/A'}
              </Text>
            </Space>
            <Button size="small" onClick={clearShipment}>Clear</Button>
          </Space>
        </Card>
      )}

      <Form.Item
        label="Notes"
        name="description"
        help="Any additional notes about this wire"
      >
        <TextArea rows={2} placeholder="Optional notes..." />
      </Form.Item>

      {mode === 'inline' && (
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createWireMutation.isPending}
            icon={<PlusOutlined />}
          >
            Record Wire Transfer
          </Button>
        </Form.Item>
      )}
    </Form>
  );

  if (mode === 'inline') {
    return formContent;
  }

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setModalVisible(true)}
      >
        Record Wire
      </Button>

      <Modal
        title={
          <Space>
            {direction === 'IN' ? (
              <ArrowDownOutlined style={{ color: '#52c41a' }} />
            ) : (
              <ArrowUpOutlined style={{ color: '#f5222d' }} />
            )}
            Record Wire Transfer
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedShipment(null);
          setShipmentSearch('');
          setDirection(defaultDirection);
        }}
        onOk={() => form.submit()}
        confirmLoading={createWireMutation.isPending}
        width={600}
        okText="Record Wire"
      >
        {formContent}
      </Modal>
    </>
  );
};

export default WireEntryForm;
